// Partner recruitment page — rendered at `/sponsor` when not logged in. SEO + conversion focused.

import React from "react";
import { Helmet } from "react-helmet-async";
import {
  BRAND,
  BRAND_SOFT,
  ACCENT,
  PictX,
  PictName,
  ConfettiBurst,
  SectionEyebrow,
  SectionH2,
  useBoominFx,
  useIsMobile,
} from "./shared";

const brand = BRAND;
const brandSoft = BRAND_SOFT;
const accent = ACCENT;

const FAQS: { q: string; a: string }[] = [
  {
    q: "本当に完全無料ですか？",
    a: "はい、現在は初期費用・月額固定費・成果報酬、すべて 0円でご利用いただけます。クーポンが何件使われても追加課金はありません。将来的に有料プランの導入を予定していますが、現在ご加盟いただいた店舗さまには、移行時にも優遇条件をご提供いたします。",
  },
  {
    q: "掲載開始までどれくらいかかりますか？",
    a: "最短3日です。お申込み後、担当者からヒアリングをさせていただき、店舗情報・クーポン内容を整えたうえで掲載開始します。即日掲載をご希望の場合もご相談ください。",
  },
  {
    q: "対応エリアはどこですか？",
    a: "2026年5月現在、渋谷区・新宿区を中心に展開しています。今後、目黒区・港区・千代田区など順次拡大予定です。エリア外でもお申込みは受け付けており、開始時に優先的にご案内します。",
  },
  {
    q: "クーポンの内容や料金は自由に決められますか？",
    a: "はい、お店ごとに自由に設計いただけます。割引・無料サービス・セット価格など、形式は問いません。法令や公序良俗に反するものを除き、お店の運営にあったクーポンをご提案ください。",
  },
  {
    q: "途中で解約はできますか？違約金はかかりますか？",
    a: "いつでも解約可能です。最低契約期間や違約金は一切ありません。現在はご利用費も発生していないため、リスクなお試しいただけます。",
  },
  {
    q: "個人店でも申し込めますか？",
    a: "もちろんです。むしろ Boomin' は個人店・小規模店舗が得意なサービスです。チラシや大手サイトでは届かない「徒歩圏内のお客さま」に直接アプローチできます。",
  },
  {
    q: "クーポンが悪用されることはありませんか？",
    a: "原則「お一人さま1回」「画面の30秒タイマー」など、不正利用が起きにくい設計です。万一の不正があった場合は当社が補償します。詳しくは導入時にご説明します。",
  },
  {
    q: "管理画面はパソコンが必要ですか？",
    a: "いいえ。スマートフォン1台で全機能をご利用いただけます。専用アプリ（iOS/Android）または管理用Webサイトから、いつでもクーポンの公開・編集が可能です。",
  },
  {
    q: "効果がでなかった場合はどうなりますか？",
    a: "現在は費用が発生しないため、長期掲載してもリスクはございません。また、3か月ごとに専属担当が運用レビューを行い、配信時間帯やクーポン内容の改善提案をさせていただきます。",
  },
  {
    q: "請求書払いは対応していますか？",
    a: "現在は加盟費用が発生しないため、請求・お支払い手続きはございません。今後有料プランを導入する際には、クレジットカード・口座振替・請求書払い（チェーン向け）に対応予定です。",
  },
];

export const PartnersPage: React.FC = () => {
  useBoominFx();
  const isMobile = useIsMobile(720);

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <Helmet>
        <title>加盟店募集 | Boomin' — 完全無料で始める、近所のお客さまへのクーポン配信</title>
        <meta
          name="description"
          content="Boomin' は半径500m以内の「いま使えるクーポン」をお客さまに届ける、ハイパーローカルな集客サービス。掲載は完全無料、成果報酬もゼロ。最短3日で掲載開始。"
        />
        <meta property="og:title" content="加盟店募集 | Boomin'" />
        <meta
          property="og:description"
          content="掲載は完全無料、成果報酬もゼロ。徒歩圏内のお客さまに、いま使えるクーポンを届けるBoomin'の加盟店募集ページ。"
        />
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </Helmet>

      <div
        data-screen-label="Partners"
        style={{
          background: "#FFFBF7",
          width: "100%",
          minHeight: "100%",
          color: "#1A1A1A",
          fontFamily:
            '"Noto Sans JP", "Hiragino Sans", "Hiragino Kaku Gothic ProN", system-ui, sans-serif',
        }}
      >
        <PNav isMobile={isMobile} />
        <PHero isMobile={isMobile} />
        <PBenefits isMobile={isMobile} />
        <PHowItWorks isMobile={isMobile} />
        <PPricing isMobile={isMobile} />
        <PFAQ isMobile={isMobile} />
        <PApplyCTA isMobile={isMobile} />
        <PFooter isMobile={isMobile} />
      </div>
    </>
  );
};

// ── Sticky nav ────────────────────────────────────────────────────────────
const PNav: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const pad = isMobile ? 20 : 64;
  return (
    <header
      style={{
        padding: `${isMobile ? 14 : 20}px ${pad}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid #F0E6DC",
        background: "rgba(255,251,247,0.96)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        position: "sticky",
        top: 0,
        zIndex: 30,
      }}
    >
      <a
        href="/"
        style={{
          fontSize: isMobile ? 20 : 24,
          fontWeight: 800,
          letterSpacing: "-0.01em",
          display: "flex",
          alignItems: "center",
          gap: 8,
          textDecoration: "none",
          color: "#1A1A1A",
        }}
      >
        <span
          style={{
            width: isMobile ? 28 : 32,
            height: isMobile ? 28 : 32,
            borderRadius: 8,
            background: brand,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PictX name="walk" size={isMobile ? 16 : 18} stroke="#fff" strokeWidth={2} />
        </span>
        Boomin<span style={{ color: brand }}>'</span>
        <span
          style={{
            marginLeft: 8,
            fontSize: 11,
            fontWeight: 800,
            color: brand,
            background: "#fff",
            padding: "3px 8px",
            borderRadius: 999,
            border: `1px solid ${brand}33`,
            letterSpacing: "0.04em",
          }}
        >
          FOR PARTNERS
        </span>
      </a>
      {!isMobile && (
        <nav style={{ display: "flex", gap: 32, fontSize: 14, fontWeight: 700 }}>
          {(
            [
              ["メリット", "#benefits"],
              ["仕組み", "#how"],
              ["料金", "#pricing"],
              ["よくある質問", "#faq"],
            ] as [string, string][]
          ).map(([l, h]) => (
            <a key={l} href={h} style={{ color: "#555", textDecoration: "none" }}>
              {l}
            </a>
          ))}
        </nav>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 12 }}>
        <a
          href="/sponsor/login"
          style={{
            color: "#1A1A1A",
            textDecoration: "none",
            borderRadius: 999,
            padding: isMobile ? "8px 12px" : "11px 18px",
            fontSize: isMobile ? 12 : 13,
            fontWeight: 700,
            border: "1.5px solid rgba(0,0,0,0.12)",
            background: "#fff",
          }}
        >
          ログイン
        </a>
        <a
          href="/sponsor/signup"
          style={{
            background: brand,
            color: "#fff",
            textDecoration: "none",
            borderRadius: 999,
            padding: isMobile ? "8px 14px" : "11px 20px",
            fontSize: isMobile ? 12 : 13,
            fontWeight: 800,
            boxShadow: `0 6px 14px ${brand}55`,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {isMobile ? "申込" : "無料で申し込む"}
          {!isMobile && <PictX name="chevron" size={14} stroke="#fff" strokeWidth={2.4} />}
        </a>
      </div>
    </header>
  );
};

// ── Hero ──────────────────────────────────────────────────────────────────
const PHero: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const pad = isMobile ? 24 : 80;
  return (
    <section
      style={{
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(180deg, ${brandSoft} 0%, #FFFBF7 100%)`,
        padding: `${isMobile ? 48 : 96}px ${pad}px ${isMobile ? 56 : 96}px`,
      }}
    >
      <ConfettiBurst
        density={isMobile ? 14 : 22}
        palette={[brand, accent, "#FFE05A", "#FFC9B0"]}
      />

      <div
        style={{
          position: "relative",
          maxWidth: 1280,
          margin: "0 auto",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          gap: isMobile ? 32 : 56,
        }}
      >
        <div style={{ flex: "1 1 0%", minWidth: 0 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "#fff",
              padding: "7px 14px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 800,
              color: brand,
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
              border: `1px solid ${brand}22`,
              marginBottom: 24,
            }}
          >
            <PictX name="sparkle" size={14} stroke={brand} strokeWidth={2.2} />
            加盟店募集中・完全無料
          </div>
          <h1
            style={{
              fontSize: isMobile ? 40 : 72,
              fontWeight: 800,
              lineHeight: 1.1,
              margin: 0,
              letterSpacing: "-0.01em",
              color: "#1A1A1A",
            }}
          >
            近所の
            <br />
            <span
              style={{
                background: `linear-gradient(transparent 65%, ${brand}30 65%)`,
                paddingRight: isMobile ? 6 : 12,
              }}
            >
              “歩いてすぐ”
            </span>
            を、
            <br />
            お客さまに<span style={{ color: brand }}>。</span>
          </h1>
          <p
            style={{
              marginTop: isMobile ? 20 : 28,
              fontSize: isMobile ? 15 : 18,
              color: "#444",
              lineHeight: 1.85,
              maxWidth: 540,
            }}
          >
            Boomin' は、半径500m以内の“いま使えるクーポン”だけを
            <br />
            お客さまに届ける、寄り道のためのウェブサービス。
            <br />
            <b>掲載は完全無料。成果報酬もゼロです。</b>
          </p>

          <div
            style={{
              marginTop: isMobile ? 28 : 36,
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <a
              href="/sponsor/signup"
              style={{
                background: brand,
                color: "#fff",
                textDecoration: "none",
                borderRadius: 999,
                padding: isMobile ? "14px 22px" : "18px 28px",
                fontSize: isMobile ? 14 : 16,
                fontWeight: 800,
                boxShadow: `0 12px 24px ${brand}55`,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              無料で申し込む
              <PictX name="chevron" size={16} stroke="#fff" strokeWidth={2.4} />
            </a>
            <a
              href="https://www.rebase.co.jp/#Contact"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: "#fff",
                color: "#1A1A1A",
                textDecoration: "none",
                border: "1.5px solid rgba(0,0,0,0.12)",
                borderRadius: 999,
                padding: isMobile ? "14px 22px" : "18px 28px",
                fontSize: isMobile ? 14 : 16,
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <PictX name="ticket" size={16} stroke="#1A1A1A" strokeWidth={2} />
              資料問い合わせ
            </a>
          </div>

          <div
            style={{
              marginTop: 22,
              fontSize: 12,
              color: "#666",
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            {["初期費用 0円", "月額・成果報酬 0円", "最短3日で掲載開始"].map((l) => (
              <span
                key={l}
                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                <PictX name="check" size={14} stroke="#0FAC6D" strokeWidth={2.6} />
                {l}
              </span>
            ))}
          </div>
        </div>

        <div
          style={{
            flex: isMobile ? "0 0 auto" : "0 0 460px",
            width: isMobile ? "100%" : 460,
            position: "relative",
          }}
        >
          <MerchantHeroMock isMobile={isMobile} />
        </div>
      </div>
    </section>
  );
};

const MerchantHeroMock: React.FC<{ isMobile: boolean }> = ({ isMobile }) => (
  <div
    style={{
      position: "relative",
      padding: isMobile ? 16 : 24,
      borderRadius: 28,
      background: "rgba(255,255,255,0.7)",
      border: "1px solid #F0E6DC",
      boxShadow: "0 20px 60px rgba(0,0,0,0.06)",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "6px 4px 14px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: "#FF5C5C" }} />
        <span style={{ width: 8, height: 8, borderRadius: 999, background: "#FFC325" }} />
        <span style={{ width: 8, height: 8, borderRadius: 999, background: "#0FAC6D" }} />
      </div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "#888",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <PictX name="sparkle" size={12} stroke="#888" strokeWidth={1.8} />
        管理画面イメージ
      </div>
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
      {(
        [
          { l: "今週の来店", v: "38", d: "+12", icon: "walk" },
          { l: "配信中", v: "2", d: "件", icon: "ticket" },
          { l: "利用率", v: "24%", d: "+4", icon: "sparkle" },
        ] as { l: string; v: string; d: string; icon: PictName }[]
      ).map((t) => (
        <div
          key={t.l}
          style={{
            background: "#fff",
            borderRadius: 14,
            padding: "12px 12px 10px",
            border: "1px solid #F4ECE2",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: 10,
              color: "#888",
              fontWeight: 700,
            }}
          >
            <PictX name={t.icon} size={11} stroke="#888" strokeWidth={1.9} />
            {t.l}
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1, marginTop: 6 }}>
            {t.v}
            <span
              style={{
                fontSize: 11,
                color: "#0FAC6D",
                fontWeight: 700,
                marginLeft: 4,
              }}
            >
              {t.d}
            </span>
          </div>
        </div>
      ))}
    </div>

    <div
      style={{
        marginTop: 12,
        background: "#fff",
        borderRadius: 14,
        padding: "14px 16px",
        border: "1px solid #F4ECE2",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 10,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 800, color: "#1A1A1A" }}>来店推移</div>
        <div style={{ fontSize: 10, color: "#888" }}>過去7日間</div>
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 56 }}>
        {[14, 22, 18, 28, 20, 30, 38].map((v, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: `${(v / 38) * 100}%`,
              background: i === 6 ? brand : `${brand}55`,
              borderRadius: 4,
              position: "relative",
            }}
          >
            {i === 6 && (
              <div
                style={{
                  position: "absolute",
                  top: -16,
                  left: "50%",
                  transform: "translateX(-50%)",
                  fontSize: 9,
                  fontWeight: 800,
                  color: brand,
                }}
              >
                38
              </div>
            )}
          </div>
        ))}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 8,
          color: "#aaa",
          marginTop: 6,
        }}
      >
        {["月", "火", "水", "木", "金", "土", "日"].map((d, i) => (
          <span
            key={i}
            style={{
              flex: 1,
              textAlign: "center",
              fontWeight: i === 6 ? 800 : 500,
              color: i === 6 ? brand : "#aaa",
            }}
          >
            {d}
          </span>
        ))}
      </div>
    </div>

    <div
      style={{
        position: "absolute",
        right: isMobile ? -8 : -28,
        bottom: isMobile ? -16 : -24,
        background: "#fff",
        borderRadius: 16,
        padding: "10px 14px",
        border: "1px solid #F0E6DC",
        boxShadow: "0 16px 40px rgba(0,0,0,0.12)",
        display: "flex",
        alignItems: "center",
        gap: 10,
        transform: "rotate(-3deg)",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: `linear-gradient(135deg, ${brand} 0%, ${accent} 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <PictX name="ticket" size={20} stroke="#fff" strokeWidth={2} />
      </div>
      <div>
        <div style={{ fontSize: 9, color: "#888", fontWeight: 700 }}>新しい来店</div>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#1A1A1A" }}>
          たった今、+1名
        </div>
      </div>
    </div>
  </div>
);

// ── Benefits ──────────────────────────────────────────────────────────────
const PBenefits: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const pad = isMobile ? 24 : 80;
  const benefits: { icon: PictName; tag: string; title: string; body: string }[] = [
    {
      icon: "walk",
      tag: "リーチ",
      title: "「いま近くにいる人」だけに届く",
      body:
        "チラシや広告と違い、Boomin'は徒歩圏内のお客さまにだけ表示。来店につながりやすい人に絞って告知できます。",
    },
    {
      icon: "yen",
      tag: "コスト",
      title: "掲載も成果報酬も、完全無料",
      body:
        "初期費用・月額固定費・成果報酬、すべて0円。お試しでも、本格運用でも、費用は一切いただきません。",
    },
    {
      icon: "clock",
      tag: "運用",
      title: "スマホから3タップで配信",
      body:
        "専用アプリで「いまから2時間だけ半額」のような短時間クーポンも、空いた瞬間にすぐ配信できます。",
    },
    {
      icon: "sparkle",
      tag: "体験",
      title: "お客さまも嬉しい、お店も嬉しい",
      body:
        "会員登録もアプリDLも不要。お客さまは画面を見せるだけ、お店はタイマー1つで確認完了。",
    },
    {
      icon: "flame",
      tag: "時間帯",
      title: "空席・空き時間を埋められる",
      body:
        "平日14時のアイドルタイム、雨の日の閑散時など、「いま暇な時間」だけ集客するピンポイント配信が得意です。",
    },
    {
      icon: "ticket",
      tag: "ファン化",
      title: "初回来店からリピーターへ",
      body:
        "一度使ったクーポンの店舗は「お気に入り」に保存される設計。1回限りでなく、近所の常連を育てる仕掛けです。",
    },
  ];
  return (
    <section
      id="benefits"
      style={{
        padding: `${isMobile ? 64 : 120}px ${pad}px`,
        background: "#FFFBF7",
      }}
    >
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <SectionEyebrow>WHY BOOMIN'</SectionEyebrow>
        <SectionH2 isMobile={isMobile} large>
          選ばれる<span style={{ color: brand }}>6つの理由</span>。
        </SectionH2>
        <p
          style={{
            marginTop: 14,
            fontSize: isMobile ? 14 : 16,
            color: "#666",
            lineHeight: 1.8,
            maxWidth: 640,
          }}
        >
          紙のチラシ、ポータルサイト、SNS広告——どれも違う「ハイパーローカルな来店」に特化したサービスです。
        </p>
        <div
          style={{
            marginTop: isMobile ? 36 : 56,
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: isMobile ? 14 : 20,
          }}
        >
          {benefits.map((b, i) => (
            <article
              key={i}
              style={{
                background: "#fff",
                borderRadius: 20,
                padding: isMobile ? 22 : 28,
                border: "1px solid #F0E6DC",
              }}
            >
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 14,
                  background: brandSoft,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 18,
                }}
              >
                <PictX name={b.icon} size={28} stroke={brand} strokeWidth={1.9} />
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: brand,
                  letterSpacing: "0.16em",
                  marginBottom: 6,
                }}
              >
                {b.tag.toUpperCase()}
              </div>
              <h3
                style={{
                  fontSize: isMobile ? 17 : 20,
                  fontWeight: 800,
                  margin: 0,
                  marginBottom: 10,
                  lineHeight: 1.35,
                  letterSpacing: "-0.005em",
                }}
              >
                {b.title}
              </h3>
              <p style={{ fontSize: 13, color: "#666", lineHeight: 1.8, margin: 0 }}>
                {b.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── How it works ──────────────────────────────────────────────────────────
const PHowItWorks: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const pad = isMobile ? 24 : 80;
  const steps = [
    {
      n: 1,
      title: "無料で申し込み",
      body: "フォームに店舗情報を入力。最短当日中に担当より折り返しご連絡します。",
    },
    {
      n: 2,
      title: "初回ミーティング",
      body: "オンラインで30分。クーポンの設計や運用のコツをお打ち合わせします。",
    },
    {
      n: 3,
      title: "管理画面で配信",
      body: "専用のアプリ／ブラウザから、好きなタイミングでクーポンを公開・更新。",
    },
    {
      n: 4,
      title: "来店・効果測定",
      body:
        "配信中はリアルタイムで来店数や利用率を確認。費用は一切かからないので、安心して試せます。",
    },
  ];
  return (
    <section
      id="how"
      style={{
        padding: `${isMobile ? 64 : 120}px ${pad}px`,
        background: brandSoft,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ maxWidth: 1280, margin: "0 auto", position: "relative" }}>
        <SectionEyebrow>HOW IT WORKS</SectionEyebrow>
        <SectionH2 isMobile={isMobile} large>
          掲載まで、<span style={{ color: brand }}>最短3日</span>。
        </SectionH2>
        <div
          style={{
            marginTop: isMobile ? 36 : 60,
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
            gap: isMobile ? 14 : 18,
            position: "relative",
          }}
        >
          {!isMobile && (
            <div
              style={{
                position: "absolute",
                top: 36,
                left: "12%",
                right: "12%",
                borderTop: `2px dashed ${brand}55`,
                zIndex: 0,
              }}
            />
          )}
          {steps.map((s) => (
            <article
              key={s.n}
              style={{
                position: "relative",
                zIndex: 1,
                background: "#fff",
                borderRadius: 20,
                padding: isMobile ? 22 : 26,
                border: "1px solid #F0E6DC",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  background: brand,
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 800,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 14,
                  boxShadow: `0 6px 14px ${brand}66`,
                }}
              >
                {s.n}
              </div>
              <h3
                style={{
                  fontSize: isMobile ? 16 : 18,
                  fontWeight: 800,
                  margin: 0,
                  marginBottom: 8,
                  letterSpacing: "-0.005em",
                }}
              >
                {s.title}
              </h3>
              <p style={{ fontSize: 13, color: "#666", lineHeight: 1.75, margin: 0 }}>
                {s.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Pricing ───────────────────────────────────────────────────────────────
const PPricing: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const pad = isMobile ? 24 : 80;
  const features = [
    "掲載クーポン無制限",
    "スマホアプリ／Webから配信・更新",
    "来店数・利用率のリアルタイム解析",
    "専属の運用アドバイザー",
    "メール・チャットサポート",
  ];
  return (
    <section
      id="pricing"
      style={{
        padding: `${isMobile ? 64 : 120}px ${pad}px`,
        background: "#fff",
      }}
    >
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <SectionEyebrow>PRICING</SectionEyebrow>
        <SectionH2 isMobile={isMobile} large>
          いまだけ、<span style={{ color: brand }}>完全無料</span>。
        </SectionH2>
        <p
          style={{
            marginTop: 14,
            fontSize: isMobile ? 14 : 16,
            color: "#666",
            lineHeight: 1.8,
            maxWidth: 720,
          }}
        >
          初期費用・月額固定費・成果報酬、すべて0円。
          サービス立ち上げ期につき、加盟店さまには費用を一切いただかず提供しています。
        </p>

        <div
          style={{
            marginTop: isMobile ? 32 : 56,
            background: "#1A1A1A",
            color: "#fff",
            borderRadius: 28,
            padding: isMobile ? "32px 24px" : "52px 56px",
            position: "relative",
            overflow: "hidden",
            border: `2px solid ${brand}`,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -120,
              right: -120,
              width: 320,
              height: 320,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${brand}66 0%, transparent 70%)`,
              pointerEvents: "none",
            }}
          />

          <div
            style={{
              position: "relative",
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: isMobile ? 32 : 48,
              alignItems: "center",
            }}
          >
            <div>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: brand,
                  color: "#fff",
                  padding: "6px 14px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                  marginBottom: 24,
                  boxShadow: `0 6px 14px ${brand}66`,
                }}
              >
                <PictX name="sparkle" size={12} stroke="#fff" strokeWidth={2.4} />
                LAUNCH CAMPAIGN
              </div>

              <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                <span
                  style={{
                    fontSize: isMobile ? 80 : 128,
                    fontWeight: 800,
                    lineHeight: 0.9,
                    letterSpacing: "-0.03em",
                    color: brand,
                  }}
                >
                  ¥0
                </span>
                <span
                  style={{
                    fontSize: 13,
                    color: "#aaa",
                    marginTop: 14,
                    fontWeight: 600,
                  }}
                >
                  / 全店舗
                </span>
              </div>

              <div
                style={{
                  marginTop: 16,
                  fontSize: isMobile ? 16 : 18,
                  fontWeight: 700,
                  lineHeight: 1.6,
                }}
              >
                初期費用も、月額も、
                <br />
                成果報酬も<span style={{ color: brand }}>すべて0円</span>。
              </div>
              <div style={{ marginTop: 12, fontSize: 12, color: "#aaa", lineHeight: 1.7 }}>
                クーポンがどれだけ使われても、追加費用は一切発生しません。
              </div>

              <a
                href="/sponsor/signup"
                style={{
                  marginTop: 28,
                  display: "inline-flex",
                  background: brand,
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: 999,
                  padding: "16px 28px",
                  fontSize: 15,
                  fontWeight: 800,
                  boxShadow: `0 12px 24px ${brand}55`,
                  alignItems: "center",
                  gap: 6,
                }}
              >
                無料で申し込む
                <PictX name="chevron" size={16} stroke="#fff" strokeWidth={2.6} />
              </a>
            </div>

            <div
              style={{
                background: "#262626",
                borderRadius: 20,
                padding: isMobile ? 22 : 28,
                border: "1px solid #333",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: brand,
                  letterSpacing: "0.16em",
                  marginBottom: 14,
                }}
              >
                含まれるもの
              </div>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                {features.map((f, i) => (
                  <li
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      fontSize: 14,
                      color: "#ddd",
                      lineHeight: 1.6,
                    }}
                  >
                    <PictX name="check" size={18} stroke={brand} strokeWidth={2.6} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div
          style={{
            marginTop: isMobile ? 24 : 32,
            fontSize: 12,
            color: "#888",
            textAlign: "center",
            lineHeight: 1.8,
          }}
        >
          ※ 将来的に有料プランの導入を予定していますが、現在ご加盟いただいた店舗さまは、移行時にも優遇条件をご提供します。
        </div>
      </div>
    </section>
  );
};

// ── FAQ ───────────────────────────────────────────────────────────────────
const PFAQ: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const pad = isMobile ? 24 : 80;
  return (
    <section
      id="faq"
      style={{
        padding: `${isMobile ? 64 : 120}px ${pad}px`,
        background: brandSoft,
      }}
    >
      <div style={{ maxWidth: 920, margin: "0 auto" }}>
        <SectionEyebrow>FAQ</SectionEyebrow>
        <SectionH2 isMobile={isMobile} large>
          よくある<span style={{ color: brand }}>質問</span>。
        </SectionH2>
        <div
          style={{
            marginTop: isMobile ? 32 : 48,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {FAQS.map((f, i) => (
            <details
              key={i}
              open={i < 2}
              style={{
                background: "#fff",
                borderRadius: 16,
                border: "1px solid #F0E6DC",
                padding: isMobile ? "16px 18px" : "20px 24px",
              }}
            >
              <summary
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  cursor: "pointer",
                  listStyle: "none",
                  outline: "none",
                }}
              >
                <span
                  style={{
                    flex: "0 0 auto",
                    width: 26,
                    height: 26,
                    borderRadius: 8,
                    background: brand,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 800,
                  }}
                >
                  Q
                </span>
                <span
                  style={{
                    flex: 1,
                    fontSize: isMobile ? 14 : 15,
                    fontWeight: 700,
                    lineHeight: 1.6,
                    color: "#1A1A1A",
                    paddingTop: 3,
                  }}
                >
                  {f.q}
                </span>
                <PictX name="plus" size={18} stroke="#888" strokeWidth={2} />
              </summary>
              <div
                style={{
                  marginTop: 14,
                  paddingLeft: 38,
                  fontSize: isMobile ? 13 : 14,
                  lineHeight: 1.85,
                  color: "#555",
                }}
              >
                {f.a}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Apply CTA + form ──────────────────────────────────────────────────────
const PApplyCTA: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const pad = isMobile ? 24 : 80;
  return (
    <section
      id="apply"
      style={{
        position: "relative",
        overflow: "hidden",
        padding: `${isMobile ? 64 : 120}px ${pad}px`,
        background: `linear-gradient(135deg, ${brand} 0%, ${accent} 100%)`,
      }}
    >
      <ConfettiBurst density={22} palette={["#fff", "#FFE05A", "#FFC9B0"]} />
      <div
        style={{
          position: "relative",
          maxWidth: 1080,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: isMobile ? 36 : 56,
          alignItems: "center",
        }}
      >
        <div>
          <h2
            style={{
              fontSize: isMobile ? 32 : 56,
              fontWeight: 800,
              lineHeight: 1.15,
              color: "#fff",
              margin: 0,
              letterSpacing: "-0.01em",
            }}
          >
            あなたのお店の
            <br />
            “歩いてすぐ”を、
            <br />
            必要な人に。
          </h2>
          <p
            style={{
              marginTop: isMobile ? 18 : 24,
              fontSize: isMobile ? 14 : 16,
              color: "rgba(255,255,255,0.92)",
              lineHeight: 1.85,
            }}
          >
            申込みは30秒。担当者から最短当日中にご連絡します。
            <br />
            まずは資料だけでも、お気軽にどうぞ。
          </p>
          <div
            style={{
              marginTop: 24,
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              fontSize: 12,
              color: "#fff",
              fontWeight: 700,
            }}
          >
            {["初期費用 0円", "解約金 なし", "最短3日掲載"].map((l) => (
              <span
                key={l}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  background: "rgba(255,255,255,0.18)",
                  padding: "6px 12px",
                  borderRadius: 999,
                  backdropFilter: "blur(4px)",
                }}
              >
                <PictX name="check" size={14} stroke="#fff" strokeWidth={2.6} />
                {l}
              </span>
            ))}
          </div>
        </div>

        <PApplyCard isMobile={isMobile} />
      </div>
    </section>
  );
};

const PApplyCard: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 22,
        padding: isMobile ? 26 : 36,
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 800,
          color: brand,
          letterSpacing: "0.16em",
          marginBottom: 4,
        }}
      >
        FREE APPLICATION
      </div>
      <h3
        style={{
          fontSize: isMobile ? 22 : 26,
          fontWeight: 800,
          margin: 0,
          marginBottom: 12,
          letterSpacing: "-0.005em",
        }}
      >
        無料で申し込む
      </h3>
      <p
        style={{
          fontSize: 13,
          color: "#666",
          lineHeight: 1.8,
          margin: 0,
          marginBottom: 22,
        }}
      >
        メールアドレスとパスワードだけで、いますぐ加盟店アカウントを作成できます。
        登録後は管理画面から店舗情報・クーポンを設定してください。
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <a
          href="/sponsor/signup"
          style={{
            background: brand,
            color: "#fff",
            border: 0,
            textDecoration: "none",
            borderRadius: 999,
            padding: "16px 24px",
            fontSize: 15,
            fontWeight: 800,
            boxShadow: `0 12px 24px ${brand}55`,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          無料で新規登録する
          <PictX name="chevron" size={16} stroke="#fff" strokeWidth={2.6} />
        </a>
        <a
          href="/sponsor/login"
          style={{
            background: "#fff",
            color: "#1A1A1A",
            textDecoration: "none",
            border: "1.5px solid rgba(0,0,0,0.14)",
            borderRadius: 999,
            padding: "14px 24px",
            fontSize: 14,
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          既にアカウントをお持ちの方はログイン
        </a>
      </div>

      <div
        style={{
          marginTop: 18,
          fontSize: 11,
          color: "#888",
          textAlign: "center",
          lineHeight: 1.8,
        }}
      >
        登録によって
        <a
          href="https://storage.instabase.jp/boomin_term.pdf"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: brand, textDecoration: "underline" }}
        >
          利用規約
        </a>
        および
        <a
          href="https://www.rebase.co.jp/privacy_policy/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: brand, textDecoration: "underline" }}
        >
          プライバシーポリシー
        </a>
        に同意したものとみなします。
      </div>
    </div>
  );
};

// ── Footer ────────────────────────────────────────────────────────────────
const PFooter: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const pad = isMobile ? 24 : 80;
  return (
    <footer
      style={{
        padding: `${isMobile ? 48 : 60}px ${pad}px`,
        background: "#1A1A1A",
        color: "#aaa",
      }}
    >
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr 1fr"
              : "minmax(220px, 1fr) repeat(3, 1fr)",
            gap: isMobile ? 28 : 40,
          }}
        >
          <div style={{ gridColumn: isMobile ? "span 2" : "auto" }}>
            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: "#fff",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: brand,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PictX name="walk" size={16} stroke="#fff" strokeWidth={2} />
              </span>
              Boomin<span style={{ color: brand }}>'</span>
            </div>
            <p style={{ fontSize: 12, lineHeight: 1.7, margin: 0, maxWidth: 280 }}>
              いま、このあたりで使えるクーポンが見つかる、寄り道のためのウェブサービス。
            </p>
          </div>
          <PFooterCol
            title="加盟店向け"
            links={[
              ["メリット", "/sponsor#benefits"],
              ["料金", "/sponsor#pricing"],
              ["導入事例", "/sponsor#voices"],
              ["FAQ", "/sponsor#faq"],
              ["お申込み", "/sponsor#apply"],
            ]}
          />
          <PFooterCol
            title="ユーザー向け"
            links={[
              ["トップ", "/"],
              ["エリア", "/#areas"],
              ["カテゴリ", "/#coupons"],
              ["使い方", "/#how"],
            ]}
          />
          <PFooterCol
            title="会社情報"
            links={[
              ["会社概要", "/about"],
              ["お知らせ", "/news"],
              ["お問い合わせ", "/contact"],
              ["採用情報", "/careers"],
            ]}
          />
        </div>
        <div
          style={{
            marginTop: isMobile ? 32 : 48,
            paddingTop: 24,
            borderTop: "1px solid #333",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            gap: 8,
            fontSize: 11,
          }}
        >
          <div>© 2026 Rebase, Inc. All rights reserved.</div>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            <a href="/sponsor" style={{ color: "#aaa", textDecoration: "none" }}>
              クーポンを掲載する
            </a>
            <a
              href="https://www.rebase.co.jp/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#aaa", textDecoration: "none" }}
            >
              運営会社
            </a>
            <a
              href="https://storage.instabase.jp/boomin_term.pdf"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#aaa", textDecoration: "none" }}
            >
              利用規約
            </a>
            <a
              href="https://www.rebase.co.jp/privacy_policy/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#aaa", textDecoration: "none" }}
            >
              プライバシーポリシー
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const PFooterCol: React.FC<{ title: string; links: [string, string][] }> = ({
  title,
  links,
}) => (
  <div>
    <div
      style={{
        fontSize: 11,
        fontWeight: 800,
        color: "#fff",
        letterSpacing: "0.14em",
        marginBottom: 14,
      }}
    >
      {title}
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {links.map(([label, href]) => (
        <a
          key={label}
          href={href}
          style={{ color: "#aaa", fontSize: 13, textDecoration: "none" }}
        >
          {label}
        </a>
      ))}
    </div>
  </div>
);

export default PartnersPage;
